import type { FilterState } from './filterTypes';

/**
 * The SRP's effective filter set: a category the renter set in the URL wins,
 * otherwise it inherits the current slug's default. Mirrors the BE's
 * `effectiveFilters`, so `/pet-friendly-…` shows the right chips with an empty
 * query string.
 *
 * Lives outside the providers so the server can compute the same set — the
 * client used to own this, which meant the server could not tell that a slug SRP
 * had any filters at all.
 */
export function mergeSlugDefaultsWithExplicit(
  slugDefaults: FilterState,
  explicit: FilterState,
  explicitKeys: ReadonlySet<string>,
): FilterState {
  const pick = <K extends keyof FilterState>(urlKey: string, field: K): FilterState[K] =>
    explicitKeys.has(urlKey) ? explicit[field] : slugDefaults[field];
  return {
    price: pick('price', 'price'),
    bedrooms: pick('beds', 'bedrooms'),
    bathrooms: pick('baths', 'bathrooms'),
    pets: pick('pets', 'pets'),
    laundry: pick('laundry', 'laundry'),
    amenities: pick('amenities', 'amenities'),
    furnished: pick('furnished', 'furnished'),
    propertyTypes: pick('propertyTypes', 'propertyTypes'),
    sqft: pick('sqft', 'sqft'),
    createdWithin: pick('createdWithin', 'createdWithin'),
    rentalTypes: pick('rentalTypes', 'rentalTypes'),
    restrictions: pick('restrictions', 'restrictions'),
    moveInDate: pick('avail', 'moveInDate'),
    keywords: pick('keywords', 'keywords'),
    additionalOpts: pick('additionalOpts', 'additionalOpts'),
    // Sentinel key is enough — commute deserialization is all-or-nothing.
    commute: pick('commuteLats', 'commute'),
    orderBy: pick('orderBy', 'orderBy'),
  };
}
